import SuperFetch from 'library/helpers/superFetch';
import { removeEmptyOrNull } from 'library/helpers/utility';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { notificationsActions } from './slice';

/**
 * Notifications saga (D5a). All reads/mutations go to the D4/D-amend v5 inbox API
 * (`/v5/:orgID/notifications/inbox*`), org-scoped server-side. The mutation reducers
 * update optimistically; these watchers call the API and — on failure — re-sync the
 * server counts (the badges' source of truth) and surface a toast.
 */

const PAGE_SIZE = 20;
// Each surface maps to a backend `state` filter: the Bell inbox shows everything (read
// state drives the dot, not the query); the Alerts surface shows unresolved attention.
const SURFACE_STATE = { inbox: 'all', attention: 'unresolved' };

const selectOrgID = (state) => state.Auth.selectedProfile.orgID;
const okStatus = (res) => res && (res.statusCode === 200 || res.statusCode === 201);

function* listParams(orgID, surface, beforeID) {
	const filters = (yield select((state) => state.Notifications.filters[orgID])) || {};
	return removeEmptyOrNull({
		limit: PAGE_SIZE,
		state: SURFACE_STATE[surface],
		beforeID: beforeID || undefined,
		category: filters.category || undefined,
		severity: filters.severity || undefined,
		siteID: filters.siteID || undefined
	});
}

function* fetchList(action) {
	const { surface } = action.payload;
	const orgID = yield select(selectOrgID);
	yield put(notificationsActions.loadListStart({ surface, orgID }));
	try {
		const params = yield* listParams(orgID, surface, null);
		const res = yield call(() => SuperFetch.get('/notifications/inbox', {}, params));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to load notifications.');
		yield put(
			notificationsActions.loadListSuccess({
				surface,
				orgID,
				items: res.data || [],
				nextCursor: res.pagination?.nextCursor ?? null,
				append: false
			})
		);
	} catch (error) {
		yield put(notificationsActions.loadListError({ surface, orgID, error: error.message || String(error) }));
	}
}

function* fetchMore(action) {
	const { surface } = action.payload;
	const orgID = yield select(selectOrgID);
	const bucket = yield select((state) => state.Notifications.lists[surface]?.[orgID]);
	if (!bucket || bucket.loading || bucket.exhausted || bucket.nextCursor == null) return;
	yield put(notificationsActions.loadListStart({ surface, orgID }));
	try {
		const params = yield* listParams(orgID, surface, bucket.nextCursor);
		const res = yield call(() => SuperFetch.get('/notifications/inbox', {}, params));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to load notifications.');
		yield put(
			notificationsActions.loadListSuccess({
				surface,
				orgID,
				items: res.data || [],
				nextCursor: res.pagination?.nextCursor ?? null,
				append: true
			})
		);
	} catch (error) {
		yield put(notificationsActions.loadListError({ surface, orgID, error: error.message || String(error) }));
	}
}

function* fetchCounts() {
	const orgID = yield select(selectOrgID);
	// No org in scope yet (first boot / reload attaches it later in switchProfile) — skip rather than
	// fire /v5/undefined/… (404s) and key the counts under `undefined`. The AlertHubIndicators effect
	// re-fires once orgID lands, and the socket ALERT_UPDATED re-syncs thereafter.
	if (!orgID) return;
	try {
		const [unreadRes, unresolvedRes] = yield all([
			call(() => SuperFetch.get('/notifications/inbox/unread-count')),
			call(() => SuperFetch.get('/notifications/inbox/unresolved-count'))
		]);
		yield put(
			notificationsActions.setCounts({
				orgID,
				unread: okStatus(unreadRes) ? (unreadRes.data?.count ?? 0) : undefined,
				unresolved: okStatus(unresolvedRes) ? (unresolvedRes.data?.count ?? 0) : undefined
			})
		);
	} catch {
		// Badge counts are non-critical — leave the last known values rather than toasting.
	}
}

// Mutations: the reducer already applied the optimistic change; here we persist it and,
// on failure, re-sync counts from the server + toast. `reconcile` re-pulls the badges.
function* reconcile(message) {
	Notification('error', message);
	yield put(notificationsActions.loadCounts());
}

function* apiMarkRead(action) {
	try {
		const res = yield call(() => SuperFetch.post(`/notifications/inbox/${action.payload.notificationID}/read`, {}));
		if (!okStatus(res)) throw new Error();
	} catch {
		yield* reconcile('Could not mark notification read.');
	}
}

function* apiMarkAllRead() {
	try {
		const res = yield call(() => SuperFetch.post('/notifications/inbox/read-all', {}));
		if (!okStatus(res)) throw new Error();
	} catch {
		yield* reconcile('Could not mark all read.');
	}
}

function* apiDismiss(action) {
	try {
		const res = yield call(() => SuperFetch.delete(`/notifications/inbox/${action.payload.notificationID}`));
		if (!okStatus(res)) throw new Error();
	} catch {
		yield* reconcile('Could not dismiss notification.');
	}
}

function* apiResolve(action) {
	try {
		const res = yield call(() =>
			SuperFetch.post(`/notifications/inbox/${action.payload.notificationID}/resolve`, {})
		);
		if (!okStatus(res)) throw new Error();
	} catch {
		yield* reconcile('Could not resolve alert.');
	}
}

function* apiSnooze(action) {
	try {
		const res = yield call(() =>
			SuperFetch.post(`/notifications/inbox/${action.payload.notificationID}/snooze`, {
				until: action.payload.until
			})
		);
		if (!okStatus(res)) throw new Error();
	} catch {
		yield* reconcile('Could not snooze alert.');
	}
}

// Seen is ambient (no optimistic reducer / count change) — fire-and-forget; a failure
// just means the badge stays until next load, so no toast.
function* apiMarkSeen(action) {
	try {
		const body = Array.isArray(action.payload?.ids) ? { ids: action.payload.ids } : {};
		yield call(() => SuperFetch.post('/notifications/inbox/seen', body));
	} catch {
		// non-critical
	}
}

// ── Preferences (D5b) ───────────────────────────────────────────────────
function* fetchPreferences() {
	const orgID = yield select(selectOrgID);
	yield put(notificationsActions.loadPreferencesStart({ orgID }));
	try {
		const res = yield call(() => SuperFetch.get('/notifications/preferences'));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to load preferences.');
		yield put(
			notificationsActions.loadPreferencesSuccess({
				orgID,
				matrix: res.data?.matrix || [],
				masters: res.data?.masters || {},
				modules: res.data?.modules
			})
		);
	} catch (error) {
		yield put(notificationsActions.loadPreferencesError({ orgID, error: error.message || String(error) }));
	}
}

// Reset-to-defaults: POST /preferences/reset (optionally scoped to one eventType) and adopt
// the server's returned view. On failure, toast + re-pull the canonical matrix.
function* resetPreferences(action) {
	const orgID = yield select(selectOrgID);
	try {
		const body = action.payload?.eventType ? { eventType: action.payload.eventType } : {};
		const res = yield call(() => SuperFetch.post('/notifications/preferences/reset', body));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to reset preferences.');
		yield put(
			notificationsActions.updatePreferencesSuccess({
				orgID,
				matrix: res.data?.matrix,
				masters: res.data?.masters,
				modules: res.data?.modules
			})
		);
	} catch (error) {
		Notification('error', 'Could not reset notification preferences.');
		yield put(notificationsActions.updatePreferencesError({ orgID }));
		yield put(notificationsActions.loadPreferences());
	}
}

// The reducer already applied the optimistic toggle; PATCH only the changed rows and adopt
// the server's returned view. On failure, re-pull the canonical matrix (reverts the toggle).
function* savePreferences(action) {
	const orgID = yield select(selectOrgID);
	const { subscriptions, channels } = action.payload;
	try {
		const body = {};
		if (Array.isArray(subscriptions) && subscriptions.length) body.subscriptions = subscriptions;
		if (Array.isArray(channels) && channels.length) body.channels = channels;
		const res = yield call(() => SuperFetch.patch('/notifications/preferences', body));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to save preferences.');
		yield put(
			notificationsActions.updatePreferencesSuccess({
				orgID,
				matrix: res.data?.matrix,
				masters: res.data?.masters,
				modules: res.data?.modules
			})
		);
	} catch (error) {
		Notification('error', 'Could not save notification preferences.');
		yield put(notificationsActions.updatePreferencesError({ orgID }));
		yield put(notificationsActions.loadPreferences());
	}
}

// Delivery-level preset (B2): PATCH /preferences/level for one event and adopt the server view
// (channels + level recomputed). On failure, toast + re-pull the canonical matrix.
function* saveEventLevel(action) {
	const orgID = yield select(selectOrgID);
	const { eventType, level } = action.payload;
	try {
		const res = yield call(() => SuperFetch.patch('/notifications/preferences/level', { eventType, level }));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to set delivery level.');
		yield put(
			notificationsActions.updatePreferencesSuccess({
				orgID,
				matrix: res.data?.matrix,
				masters: res.data?.masters,
				modules: res.data?.modules
			})
		);
	} catch (error) {
		Notification('error', 'Could not update the delivery level.');
		yield put(notificationsActions.updatePreferencesError({ orgID }));
		yield put(notificationsActions.loadPreferences());
	}
}

// ── Admin org/site defaults editor (B2/P2b) ─────────────────────────────────
function* fetchAdminPreferences(action) {
	const { orgID, scope, siteID } = action.payload;
	yield put(notificationsActions.loadAdminPreferencesStart({ orgID, scope, siteID }));
	try {
		const query = scope === 'site' ? { scope, siteID } : { scope };
		const res = yield call(() => SuperFetch.get('/notifications/preferences/admin', {}, query));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to load admin defaults.');
		yield put(
			notificationsActions.loadAdminPreferencesSuccess({
				orgID,
				scope,
				siteID,
				events: res.data?.events || [],
				modules: res.data?.modules
			})
		);
	} catch (error) {
		yield put(
			notificationsActions.loadAdminPreferencesError({
				orgID,
				scope,
				siteID,
				error: error.message || String(error)
			})
		);
	}
}

function* saveAdminPreferences(action) {
	const { orgID, scope, siteID, cells, channelMasters } = action.payload;
	try {
		const body = scope === 'site' ? { scope, siteID } : { scope };
		if (cells) body.cells = cells;
		if (channelMasters) body.channelMasters = channelMasters; // channel-wide masters (org/site kill switch)
		const res = yield call(() => SuperFetch.patch('/notifications/preferences/admin', body));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to save admin defaults.');
		yield put(
			notificationsActions.updateAdminPreferencesSuccess({
				orgID,
				scope,
				siteID,
				events: res.data?.events,
				modules: res.data?.modules,
				channelMasters: res.data?.channelMasters
			})
		);
	} catch (error) {
		Notification('error', 'Could not save the notification defaults.');
		yield put(notificationsActions.updateAdminPreferencesError({ orgID, scope, siteID }));
		yield put(notificationsActions.loadAdminPreferences({ orgID, scope, siteID }));
	}
}

function* resetAdminPreferences(action) {
	const { orgID, scope, siteID, eventType } = action.payload;
	try {
		const body = scope === 'site' ? { scope, siteID } : { scope };
		if (eventType) body.eventType = eventType;
		const res = yield call(() => SuperFetch.post('/notifications/preferences/admin/reset', body));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to reset admin defaults.');
		yield put(
			notificationsActions.updateAdminPreferencesSuccess({
				orgID,
				scope,
				siteID,
				events: res.data?.events,
				modules: res.data?.modules
			})
		);
	} catch (error) {
		Notification('error', 'Could not reset the notification defaults.');
		yield put(notificationsActions.updateAdminPreferencesError({ orgID, scope, siteID }));
		yield put(notificationsActions.loadAdminPreferences({ orgID, scope, siteID }));
	}
}

export default function* notificationsSaga() {
	yield takeEvery(notificationsActions.loadList, fetchList);
	yield takeEvery(notificationsActions.loadMoreList, fetchMore);
	yield takeLatest(notificationsActions.loadCounts, fetchCounts);
	yield takeEvery(notificationsActions.markRead, apiMarkRead);
	yield takeEvery(notificationsActions.markAllRead, apiMarkAllRead);
	yield takeEvery(notificationsActions.dismiss, apiDismiss);
	yield takeEvery(notificationsActions.resolve, apiResolve);
	yield takeEvery(notificationsActions.snooze, apiSnooze);
	yield takeEvery(notificationsActions.markSeen, apiMarkSeen);
	yield takeLatest(notificationsActions.loadPreferences, fetchPreferences);
	yield takeEvery(notificationsActions.updatePreferences, savePreferences);
	yield takeEvery(notificationsActions.setEventLevel, saveEventLevel);
	yield takeEvery(notificationsActions.resetPreferences, resetPreferences);
	yield takeLatest(notificationsActions.loadAdminPreferences, fetchAdminPreferences);
	yield takeEvery(notificationsActions.updateAdminPreferences, saveAdminPreferences);
	yield takeEvery(notificationsActions.resetAdminPreferences, resetAdminPreferences);
}
